import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import dynamic from 'next/dynamic';
import { cn } from '@discover-lib/utils';
import { timeFromNow } from '@discover-lib/dateFormats';

const PhotoSwipe = dynamic(() => import('@/components/organisms/PhotoSwipe'), { ssr: false });

type MenuPhotoSwipeProps = {
  items: {
    src: string;
    url: string;
    w: number;
    h: number;
    orientation: 'landscape' | 'portrait' | 'even';
    page?: number;
  }[];
  isOpen: boolean;
  index: number;
  onClose: () => void;
  onChange: (index: number) => void;
  showBackground: boolean;
  /** Latest menu edit, shown as "Last updated …" while the viewer is open. */
  lastUpdated?: string | null;
};

export default function MenuPhotoSwipe({
  items,
  isOpen,
  index,
  onClose,
  onChange,
  showBackground,
  lastUpdated,
}: MenuPhotoSwipeProps) {
  const [portalRoot, setPortalRoot] = useState<HTMLElement | null>(null);

  useEffect(() => {
    setPortalRoot(document.body);
  }, []);

  if (!portalRoot || (!isOpen && !showBackground)) {
    return null;
  }

  // The modal must escape sticky card stacking contexts so page chrome stays behind it.
  return createPortal(
    <>
      <PhotoSwipe
        items={items}
        isOpen={isOpen}
        index={index}
        onClose={onClose}
        onChange={onChange}
        options={{
          closeElClasses: [],
          fullscreenEl: false,
          shareEl: false,
          history: false,
          zoomEl: false,
          timeToIdle: 1000,
          bgOpacity: 0,
          barsSize: {
            top: 10,
            bottom: 100,
          },
          tapToToggleControls: false,
          clickToCloseNonZoomable: false,
          pinchToClose: false,
          closeOnVerticalDrag: false,
        }}
      />

      {isOpen && lastUpdated && (
        // Above PhotoSwipe's own layer (z-index 1500), centred in its top bar between the counter and the close button.
        <p className="pointer-events-none fixed left-1/2 top-6 z-[1501] m-0 -translate-x-1/2 whitespace-nowrap rounded-full bg-[rgba(39,43,46,0.5)] px-3 py-1 text-xs text-white md:top-9">
          Last updated {timeFromNow(lastUpdated)}
        </p>
      )}

      {/* Custom blur background overlay */}
      {showBackground && (
        <div
          className={cn(
            'fixed inset-0 bg-white/95 backdrop-blur-sm transition-opacity duration-300',
            'z-[11]', // z-index needs to be more than 10 to show over the Overview content
            isOpen ? 'opacity-100' : 'opacity-0'
          )}
        />
      )}
    </>,
    portalRoot
  );
}
